/**
 * RoutineDraftContext — one shared draft store + actions per routine editor route instance.
 *
 * P12 §6.2: the draft is lifted to the route (`WorkflowDetail`) and provided here so BOTH the
 * editor pane (T-FE3) and the RoutineBuilder chat pane (T-FE3) read/write ONE draft — agent
 * patches and human edits land in the same place. This is a per-route-instance context (no
 * global-state smell), mirroring `WorkspaceContext` usage.
 *
 * The provider composes the two layers built in T-FE1:
 *   - {@link useRoutineDraft}  — the draft state + load/hydrate + save-state chip.
 *   - {@link useRoutineActions} — the mutations (save / dry-run / start / pause / runNow / delete).
 */
import React from 'react';

import { useRoutineActions, type UseRoutineActionsReturn } from '../hooks/useRoutineActions';
import { type RoutineDraftStore, useRoutineDraft } from '../hooks/useRoutineDraft';
import type { WorkflowDraft } from '../types';

export interface RoutineDraftContextValue {
  store: RoutineDraftStore;
  actions: UseRoutineActionsReturn;
}

const RoutineDraftCtx = React.createContext<RoutineDraftContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- context module intentionally co-exports Provider + hook; HMR-only rule, no runtime effect
export function useRoutineDraftContext(): RoutineDraftContextValue {
  const ctx = React.useContext(RoutineDraftCtx);
  if (!ctx) throw new Error('useRoutineDraftContext must be used inside RoutineDraftProvider');
  return ctx;
}

export function RoutineDraftProvider({
  workflowId,
  draftSessionKey,
  initialDraft,
  children,
}: {
  workflowId: string | undefined;
  /**
   * The client-minted `draftLocalId` the RoutineBuilder chat session is keyed under for a brand-new
   * routine — passed into `save` so the first save re-keys that session to the real id (Issue 9).
   */
  draftSessionKey?: string;
  /** Override initial draft (Storybook / tests). The live editor seeds from workflowId. */
  initialDraft?: WorkflowDraft;
  children: React.ReactNode;
}): React.ReactElement {
  const store = useRoutineDraft(workflowId, initialDraft);
  const actions = useRoutineActions(store, draftSessionKey);

  const value = React.useMemo<RoutineDraftContextValue>(() => ({ store, actions }), [store, actions]);

  return <RoutineDraftCtx.Provider value={value}>{children}</RoutineDraftCtx.Provider>;
}
